import { useState } from 'react';
import { WEB_ICO, type IconKey } from '../shell/icons';
import { ErrorState, LoadingState } from '../components/ScreenState';
import { useAsync } from '../hooks/useAsync';
import { aiApi } from '../api/ai';

function QuotaBar({ label, used, total }: { label: string; used: number; total: number }) {
  /// total = 0 (backend chưa cấp lượt) → coi là hết lượt, tránh width NaN%.
  const exhausted = total <= 0 || used >= total;
  const pct = total > 0 ? Math.min(100, Math.max(0, (used / total) * 100)) : 0;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13.5, fontWeight: 600, color: 'var(--ink)' }}>
        <span>{label}</span>
        <span style={{ color: exhausted ? 'var(--warning)' : 'var(--text-secondary)' }}>
          {exhausted ? 'Hết lượt — làm mới sau 00:00' : `còn ${total - used}/${total} lượt`}
        </span>
      </div>
      <div style={{ height: 6, borderRadius: 999, background: 'var(--border-default)', overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: exhausted ? 'var(--warning)' : 'var(--cta-default)', borderRadius: 999 }} />
      </div>
    </div>
  );
}

function ToolCard({ icon, title, sub, disabled, onClick }: { icon: IconKey; title: string; sub: string; disabled?: boolean; onClick: () => void }) {
  return (
    <button
      disabled={disabled}
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        textAlign: 'left',
        padding: 20,
        background: 'var(--surface-page)',
        border: '1px solid var(--border-default)',
        borderRadius: 'var(--radius-card)',
        boxShadow: 'var(--shadow-card)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        fontFamily: 'var(--font-vi)',
      }}
    >
      <span
        style={{
          width: 48,
          height: 48,
          borderRadius: '50%',
          background: 'rgba(0,91,150,0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {WEB_ICO[icon]('var(--cta-default)', 24)}
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 16, fontWeight: 700, color: 'var(--ink)' }}>{title}</span>
        <span style={{ display: 'block', fontSize: 13.5, color: 'var(--text-secondary)', marginTop: 3 }}>{sub}</span>
      </span>
    </button>
  );
}

export function WebAIScreen({ narrow = false, onOpenTool }: { narrow?: boolean; onOpenTool: (title: string) => void }) {
  /// useAsync chưa có refetch — đổi reloadKey để chạy lại effect.
  const [reloadKey, setReloadKey] = useState(0);
  const quota = useAsync(() => aiApi.getQuota(), [reloadKey]);
  const q = quota.data;
  const chatExhausted = !!q && (q.chatTotal <= 0 || q.chatUsed >= q.chatTotal);

  if (quota.loading) return <LoadingState />;
  if (quota.error || !q) return <ErrorState message="Không tải được dữ liệu AI" onRetry={() => setReloadKey((k) => k + 1)} />;

  return (
    <div style={{ display: 'grid', gridTemplateColumns: narrow ? '1fr' : '1fr 320px', gap: 24, alignItems: 'start', fontFamily: 'var(--font-vi)', maxWidth: 900 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <ToolCard icon="ai" title="Trò chuyện cùng AI" sub="Hỏi đáp theo ngữ cảnh bài đang học" disabled={chatExhausted} onClick={() => onOpenTool('Trò chuyện cùng AI')} />
        <ToolCard icon="mic" title="Chấm phát âm" sub="Ghi âm và nhận phản hồi chi tiết" onClick={() => onOpenTool('Chấm phát âm')} />
      </div>
      <div style={{ background: 'var(--surface-section)', borderRadius: 'var(--radius-card)', padding: 20, display: 'flex', flexDirection: 'column', gap: 18 }}>
        <QuotaBar label="Chat" used={q.chatUsed} total={q.chatTotal} />
        <QuotaBar label="Phát âm" used={q.pronunciationUsed} total={q.pronunciationTotal} />
      </div>
    </div>
  );
}
